import { removeEmptyFields } from "@gigpro/ui/Form/utils";
import type { AnalyticsSnippet } from "@segment/analytics-next";
import * as Sentry from "@sentry/react";
import type { ManagerProfile, UserResponse } from "@sidegigllc/api/client";
import { useMatches } from "@tanstack/react-router";
import { env } from "config/env";
import React from "react";
import { initBraze } from "./braze";

export type AnalyticsUserTraits = {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string | undefined | null;
  email: string | undefined | null;
  isGigproAdmin: boolean;
  business: string | undefined | null;
  appMode: string | undefined | null;
  role: "PRO" | "MANAGER";
  marketId: number | undefined | null;
  market: string | undefined | null;
  locationId: number | undefined | null;
};

export const getAnalyticsUserTraits = (
  user: UserResponse,
  managerProfile: Partial<ManagerProfile> | undefined,
  locationId: number | undefined,
): AnalyticsUserTraits => ({
  id: user.id,
  firstName: user.first_name,
  lastName: user.last_name,
  email: user.email ?? managerProfile?.email,
  phoneNumber: user.phone_number,
  business: managerProfile?.business_name,
  marketId: managerProfile?.market_id,
  locationId,
  market: managerProfile?.market_name,
  isGigproAdmin: !!user.is_gigpro_admin,
  role: "MANAGER",
  appMode: user.app_mode,
});

if (env.REACT_APP_SEGMENT_WRITE_KEY && globalThis.analytics) {
  if (env.REACT_APP_SEGMENT_CDN_URL) {
    globalThis.REACT_APP_SEGMENT_CDN_URL = env.REACT_APP_SEGMENT_CDN_URL;
  }
  globalThis.analytics._writeKey = env.REACT_APP_SEGMENT_WRITE_KEY;
  globalThis.analytics.load(env.REACT_APP_SEGMENT_WRITE_KEY);
}

const segmentAnalytics: AnalyticsSnippet = globalThis.analytics;

export const identify = async (id: number, traits: Record<string, string | number | boolean | undefined | null>) => {
  const attributes: Record<string, string> = removeEmptyFields({ ...traits, environment: env.REACT_APP_ENV });
  await segmentAnalytics?.identify(`${id}`, attributes);
  Sentry.setUser({ id, ...traits });
  await initBraze(`${id}`);
};

type EventOptions = {
  "User Logout": Record<string, never>;
  "Pro Search": {
    market_id: number;
    position_id: number | undefined;
    position_name: string;
    market_name: string;
    location_id: number;
    location_name: string;
  };
  "Pro Rating Submitted": {
    gig_id: number;
    pro_id: number;
    manager_id: number;
    location_id: number;
    market_id: number;
    pro_first_name: string;
    pro_last_name: string;
    position_name: string;
    rating_attribute: string;
    rating_score: number;
    favorited: boolean;
    feedback_provided: boolean;
  };
  [K: `Shift Posting ${string}`]: { type: string } & Record<string, string | number | boolean | null | undefined>;
  [K: `Hire ${string}`]: Record<string, string | number | boolean | null | undefined>;
  [K: `AI Chat ${string}`]: Record<string, string | number | boolean | null | undefined>;
};

export const track = <T extends keyof EventOptions>(event: T, properties: EventOptions[T]) => {
  void segmentAnalytics?.track(event, properties);
  Sentry.addBreadcrumb({ category: "action", message: event, data: properties });
};

const page = (page: string) => {
  void segmentAnalytics?.page(undefined, page);
};

const PARAM_SEGMENT = /\$(\w+)/g;
const TRAILING_SPLAT = /\$$/;

export const useRouteTracker = () => {
  const matches = useMatches();
  const pattern = (matches.at(-1)?.fullPath ?? "").replaceAll(PARAM_SEGMENT, ":$1").replace(TRAILING_SPLAT, "");

  React.useEffect(() => {
    page(pattern);
  }, [pattern]);
};
